import { CheckCircle2, Clock } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkCjkFriendly from "remark-cjk-friendly";
import type { AgendaItem } from "@/lib/parseWebinarContent";
import { useLanguage } from "@/hooks/useLanguage";
import WebinarPartnerAbout from "./WebinarPartnerAbout";

type LearnItem = string | { parent: string; children: string[] };

interface WebinarContentProps {
  problemFraming: string;
  whatYoullLearn: LearnItem[];
  keyTakeaways?: string[];
  agenda?: AgendaItem[];
  aboutTheHosts?: string;
  ourSupport?: string;
  aboutPartner?: string;
  partnerTitle?: string;
  genericBody?: string;
  /** When true the partner block is rendered elsewhere (below "Who should attend"). */
  partnerBelowAudience?: boolean;
}

const markdownComponents = {
  img: () => null as any,
  a: ({ href, children }: any) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
      {children}
    </a>
  ),
};

const PROSE_CLASSES =
  "prose prose-lg max-w-none prose-headings:text-foreground prose-p:text-foreground/75 prose-p:leading-[1.9] prose-p:text-[17px] prose-strong:text-foreground prose-strong:font-semibold [&_h2]:text-2xl [&_h2]:sm:text-3xl [&_h2]:font-bold [&_h2]:text-accent [&_h2]:mt-0 [&_h2]:mb-6 [&_h3]:text-xl [&_h3]:sm:text-2xl [&_h3]:font-bold [&_h3]:mt-8 [&_h3]:mb-3 [&_h4]:text-lg [&_h4]:font-bold [&_h4]:text-accent [&_h4]:mb-3 [&_ul]:my-4 [&_ul]:space-y-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:marker:text-primary/60 prose-li:text-foreground/75 [&_p+p]:mt-5";

const SESSION_LABEL_RE = /\*\*(SESSION|SESIÓN|SESION|会话|第[一二三四五]场)\s*([0-9IVX一二三四五]+)\*\*/i;

interface SessionBlock {
  heading: string;
  label: string | null;
  body: string;
}

/** Split problem framing markdown into per-session blocks when 2+ session cards are present. */
function splitSessions(md: string): { intro: string; sessions: SessionBlock[] } | null {
  // Split on top-level `## ` headings.
  const parts = md.split(/\n(?=##\s+)/);
  const sessions: SessionBlock[] = [];
  let intro = parts[0].startsWith("## ") ? "" : parts[0];
  const headingParts = parts[0].startsWith("## ") ? parts : parts.slice(1);
  for (const part of headingParts) {
    const m = part.match(/^##\s+([^\n]+)\n?([\s\S]*)$/);
    if (!m) continue;
    const heading = m[1].trim();
    let body = m[2] ?? "";
    const labelMatch = body.match(SESSION_LABEL_RE);
    if (!labelMatch) continue;
    const label = `${labelMatch[1].toUpperCase()} ${labelMatch[2]}`;
    // Remove the label line from body
    body = body.replace(SESSION_LABEL_RE, "").replace(/^\s*\n+/, "").trim();
    sessions.push({ heading, label, body });
  }
  if (sessions.length < 2) return null;
  // Clean intro: drop bare "Detailed Session Information" style text before first session
  intro = intro
    .replace(/^\s*(?:Detailed Session Information|Información detallada de las sesiones|详细会话信息)\s*$/im, "")
    .trim();
  return { intro, sessions };
}

interface ContentSection {
  heading: string;
  body: string;
}

/**
 * Split problem framing markdown into standalone `## Heading` sections.
 * Only applies when the markdown starts with a heading (no preamble text),
 * so multi-paragraph intros render as distinct, evenly spaced sections.
 */
function splitHeadingSections(md: string): ContentSection[] | null {
  if (!md.startsWith("## ")) return null;
  const parts = md.split(/\n(?=##\s+)/);
  const sections: ContentSection[] = [];
  for (const part of parts) {
    const m = part.match(/^##\s+([^\n]+)\n?([\s\S]*)$/);
    if (m) sections.push({ heading: m[1].trim(), body: (m[2] ?? "").trim() });
  }
  return sections.length > 0 ? sections : null;
}

const SessionCard = ({ session, index }: { session: SessionBlock; index: number }) => (
  <div className="relative rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
    <div className="h-1 w-full bg-gradient-to-r from-accent to-primary" />
    <div className="px-6 sm:px-8 py-7 sm:py-9">
      <div className="flex items-center gap-3 mb-3">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-accent/10 text-accent font-bold text-sm tabular-nums">
          {index + 1}
        </span>
        {session.label && (
          <span className="text-xs font-bold uppercase tracking-[0.14em] text-accent">
            {session.label}
          </span>
        )}
      </div>
      <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-5 leading-snug">
        {session.heading}
      </h3>
      <div className={PROSE_CLASSES}>
        <ReactMarkdown remarkPlugins={[remarkGfm, remarkCjkFriendly]} components={markdownComponents}>
          {session.body}
        </ReactMarkdown>
      </div>
    </div>
  </div>
);

const WebinarContent = ({
  problemFraming,
  whatYoullLearn,
  keyTakeaways = [],
  agenda = [],
  aboutTheHosts = "",
  ourSupport = "",
  aboutPartner = "",
  partnerTitle = "",
  genericBody = "",
  partnerBelowAudience = false,
}: WebinarContentProps) => {
  const lang = useLanguage();
  const isEs = lang === "es";
  const isZh = lang === "zh-cn";
  const tr = (en: string, es: string, zh: string) => (isZh ? zh : isEs ? es : en);
  const sessionSplit = problemFraming ? splitSessions(problemFraming) : null;
  const headingSections =
    problemFraming && !sessionSplit ? splitHeadingSections(problemFraming) : null;
  return (
    <div id="webinar-content" className="space-y-14">
      {/* Problem framing */}
      {problemFraming && (
        <div>
          {sessionSplit ? (
            <div className="space-y-8">
              {sessionSplit.intro && (
                <div className={PROSE_CLASSES}>
                  <ReactMarkdown remarkPlugins={[remarkGfm, remarkCjkFriendly]} components={markdownComponents}>
                    {sessionSplit.intro}
                  </ReactMarkdown>
                </div>
              )}
              <div className="grid grid-cols-1 gap-6 md:gap-8">
                {sessionSplit.sessions.map((s, i) => (
                  <SessionCard key={i} session={s} index={i} />
                ))}
              </div>
            </div>
          ) : headingSections ? (
            <div className="space-y-14">
              {headingSections.map((section) => (
                <div key={section.heading}>
                  <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-4">
                    {section.heading}
                  </h3>
                  <div className={PROSE_CLASSES}>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm, remarkCjkFriendly]}
                      components={markdownComponents}
                    >
                      {section.body}
                    </ReactMarkdown>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={PROSE_CLASSES}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkCjkFriendly]}
                components={markdownComponents}
              >
                {problemFraming}
              </ReactMarkdown>
            </div>
          )}
        </div>
      )}


      {/* Agenda */}
      {agenda.length > 0 && (
        <div>
          <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-6">
            {tr("Agenda", "Agenda", "议程")}
          </h3>
          <div className="space-y-3">
            {agenda.map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-4 bg-secondary/50 rounded-xl px-5 py-3.5 border border-border"
              >
                {item.timestamp ? (
                  <span className="flex items-center gap-1.5 text-accent font-mono text-sm font-semibold shrink-0 mt-0.5">
                    <Clock size={14} className="text-accent/70" />
                    {item.timestamp}
                  </span>
                ) : (
                  <CheckCircle2 size={17} className="text-accent shrink-0 mt-0.5" />
                )}
                <span className="text-foreground/80 text-sm leading-relaxed">
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Key takeaways */}
      {keyTakeaways.length > 0 && (
        <div className="bg-qbd-ice border border-qbd-sky rounded-2xl px-6 sm:px-8 py-8">
          <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-6">
            {tr(`${keyTakeaways.length} Key Takeaways`, `${keyTakeaways.length} conclusiones clave`, `${keyTakeaways.length} 个核心要点`)}
          </h3>
          <ol className="space-y-5">
            {keyTakeaways.map((item, i) => (
              <li key={i} className="flex items-start gap-3.5">
                <span className="w-7 h-7 rounded-full bg-accent text-accent-foreground text-sm font-bold flex items-center justify-center shrink-0">
                  {i + 1}
                </span>
                <span className="text-foreground/80 leading-relaxed text-[15px] pt-0.5">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm, remarkCjkFriendly]}
                    components={{
                      p: ({ children }) => <span>{children}</span>,
                      strong: ({ children }) => <strong className="text-foreground font-semibold">{children}</strong>,
                    }}
                  >
                    {item}
                  </ReactMarkdown>
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* What you'll learn */}
      {whatYoullLearn.length > 0 && (
        <div className="bg-secondary/40 border border-border rounded-2xl px-6 sm:px-8 py-8">
          <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-6">
            {tr("What you'll learn", "¿Qué aprenderás?", "您将学到什么")}
          </h3>
          <ul className="space-y-5">
            {whatYoullLearn.map((item, i) => {
              const isNested = typeof item === "object";
              const text = isNested ? item.parent : item;
              return (
                <li key={i} className="flex flex-col gap-2">
                  <div className="flex items-start gap-3.5">
                    <span className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center shrink-0 mt-0.5">
                      <CheckCircle2 size={16} className="text-accent" strokeWidth={2.5} />
                    </span>
                    <span className="text-foreground/80 leading-relaxed text-[15px]">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm, remarkCjkFriendly]}
                        components={{
                          p: ({ children }) => <span>{children}</span>,
                          strong: ({ children }) => (
                            <strong className="text-foreground font-semibold">{children}</strong>
                          ),
                        }}
                      >
                        {text}
                      </ReactMarkdown>
                    </span>
                  </div>
                  {isNested && item.children.length > 0 && (
                    <ul className="ml-[38px] space-y-2 list-disc pl-4">
                      {item.children.map((child, j) => (
                        <li key={j} className="text-foreground/70 text-[14px] leading-relaxed marker:text-primary/50">
                          {child}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* About the partner (e.g. About Capital Cell). Skipped here when the article asks for it below "Who should attend". */}
      {aboutPartner && !partnerBelowAudience && (
        <WebinarPartnerAbout aboutPartner={aboutPartner} partnerTitle={partnerTitle} />
      )}

      {/* About the hosts */}
      {aboutTheHosts && (
        <div>
          <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-4">
            {tr("About the hosts", "Sobre los ponentes", "关于主讲人")}
          </h3>
          <div className="prose prose-base max-w-none prose-p:text-foreground/75 prose-p:leading-[1.85] prose-strong:text-foreground prose-strong:font-semibold">
            <ReactMarkdown remarkPlugins={[remarkGfm, remarkCjkFriendly]}>{aboutTheHosts}</ReactMarkdown>
          </div>
        </div>
      )}

      {/* Our support */}
      {ourSupport && (
        <div>
          <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-4">
            {tr("Our support", "Nuestro soporte", "我们的支持")}
          </h3>
          <div className="prose prose-base max-w-none prose-p:text-foreground/75 prose-p:leading-[1.85] prose-strong:text-foreground prose-strong:font-semibold">
            <ReactMarkdown remarkPlugins={[remarkGfm, remarkCjkFriendly]}>{ourSupport}</ReactMarkdown>
          </div>
        </div>
      )}

      {/* Generic fallback body */}
      {genericBody && !problemFraming && whatYoullLearn.length === 0 && agenda.length === 0 && (
        <div className="prose prose-lg max-w-none prose-headings:text-foreground prose-p:text-foreground/75 prose-p:leading-[1.9] prose-p:text-[17px] prose-strong:text-foreground prose-strong:font-semibold prose-a:text-accent hover:prose-a:underline [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mt-10 [&_h2]:mb-5 [&_h3]:text-xl [&_h3]:font-bold [&_h3]:mt-8 [&_h3]:mb-4 [&_ul]:my-5 [&_ol]:my-5 prose-li:text-foreground/75">
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkCjkFriendly]}
            components={markdownComponents}
          >
            {genericBody}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
};

export default WebinarContent;
